import React, { useEffect, useState } from "react";
import { Send, Mail, Clock, CheckCircle2, Loader2, PackageSearch } from "lucide-react";
import { Link } from "react-router-dom";
import { useSite } from "../context/SiteContext";
import { sendContact } from "../lib/api";
import { toast } from "../hooks/use-toast";

const inputCls = "w-full bg-navy-700/80 border border-white/15 focus:border-gold rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors";

const InfoRow = ({ icon: I, title, children }) => (
  <div className="flex gap-4">
    <span className="h-11 w-11 shrink-0 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center text-gold"><I size={18} /></span>
    <div><p className="text-white font-semibold text-sm">{title}</p><div className="text-slate-400 text-sm mt-0.5">{children}</div></div>
  </div>
);

export default function Contact() {
  const { settings: s } = useSite();
  const cp = s?.contactPage || {};
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast({ title: "Missing details", description: "Please fill in your name, email and message." }); return; }
    setBusy(true);
    try { await sendContact(form); setSent(true); }
    catch { toast({ title: "Could not send", description: "Please try again or reach us on Telegram." }); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-navy min-h-screen py-16 md:py-24" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-gold text-sm font-semibold tracking-widest uppercase mb-3 fade-up">Contact {s?.brandName}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white fade-up"><span className="liquid-gold-text">{cp.title || "Get in Touch"}</span></h1>
        <p className="mt-5 text-slate-300 max-w-2xl fade-up">{cp.subtitle}</p>

        <div className="mt-14 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 glass-navy-card rounded-3xl p-8 space-y-7 h-fit">
            {s?.contact?.telegramLink && <InfoRow icon={Send} title="Telegram"><a href={s?.contact?.telegramLink} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="contact-telegram-link">{s?.contact?.telegramHandle}</a></InfoRow>}
            <InfoRow icon={Mail} title="Email"><a href={`mailto:${s?.contact?.supportEmail}`} className="hover:text-gold transition-colors" data-testid="contact-email-link">{s?.contact?.supportEmail}</a></InfoRow>
            <InfoRow icon={Clock} title="Support Hours">{s?.contact?.hours}</InfoRow>
            <InfoRow icon={PackageSearch} title="Track an Order"><Link to="/track" className="hover:text-gold transition-colors">Check your order status</Link></InfoRow>
          </div>

          <div className="lg:col-span-3 glass-gold-card rounded-3xl p-8">
            {sent ? (
              <div className="text-center py-12 pop-in" data-testid="contact-success">
                <CheckCircle2 size={48} className="text-emerald-400 mx-auto mb-5" />
                <h3 className="text-2xl font-bold text-white">Message sent!</h3>
                <p className="text-slate-300 mt-2">Thanks {form.name}, we'll get back to you shortly at {form.email}.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="contact-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input data-testid="contact-email-input" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <input data-testid="contact-subject-input" className={inputCls} placeholder="Subject (optional)" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                <textarea data-testid="contact-message-input" className={`${inputCls} h-40 resize-none`} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <button data-testid="contact-submit-btn" disabled={busy} className="btn-liquid w-full text-navy font-bold py-4 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  <span className="relative z-10 inline-flex items-center gap-2">{busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />} Send Message</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
